/**
 * What project-level undo covers, and what it deliberately does not.
 *
 * Undo here means "take back an engineering input". Those all live inside
 * `project.modules`: the roof geometry, the product and resistance selection,
 * the zone layouts, the module parameters, and the set of modules itself. The
 * history therefore holds module snapshots rather than whole projects.
 *
 * The project's identity — its name, location, design method and safety-factor
 * package — is not an engineering input and is not tracked. Recording it would
 * put steps in the stack that appear to do nothing when undone, and holding it
 * in the snapshot would let an undo of a roof edit quietly revert a name typed
 * afterwards.
 */

import type { RoofModule } from '../gen/ejot/roofing/v1/types';

export type TrackedModules = RoofModule[] | undefined;

/**
 * The module a step changed, so the workspace can show what just moved.
 *
 * An undo that silently alters a roof the user cannot see reads as nothing
 * having happened. Returns undefined when the change cannot be attributed to a
 * module that still exists, in which case the caller keeps its current view.
 */
export function changedModuleId(from: TrackedModules, to: TrackedModules): string | undefined {
  const before = from ?? [];
  const after = to ?? [];

  const added = firstMissing(after, before);
  if (added) return added;

  // A module the step removed cannot be shown, so fall back to what remains.
  if (firstMissing(before, after)) return after[0]?.id;

  // Updates are immutable throughout the application, so an edited module is
  // simply the one whose object identity changed.
  return after.find((module) => {
    const previous = before.find((candidate) => candidate.id === module.id);
    return previous !== undefined && previous !== module;
  })?.id;
}

function firstMissing(
  modules: readonly RoofModule[],
  within: readonly RoofModule[],
): string | undefined {
  return modules.find((module) => !within.some((candidate) => candidate.id === module.id))?.id;
}

/**
 * Whether a keyboard event should reach project undo.
 *
 * Two surfaces answer for themselves. A text field has the browser's own undo,
 * which is what someone editing text means; taking that over would jump the
 * whole project back instead of restoring the characters they just typed. The
 * plan editor keeps a finer history of drawing operations, which is the right
 * granularity while a shape is being drawn.
 */
export function undoBelongsToProject(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return true;
  if (target.isContentEditable) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return false;
  return target.closest('.plan-editor') === null;
}
